"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var proxiedFilter_1 = require("../Util/proxiedFilter");
var proxiedFind_1 = require("../Util/proxiedFind");
var proxiedIndexOf_1 = require("../Util/proxiedIndexOf");
var proxiedMap_1 = require("../Util/proxiedMap");
var proxiedPush_1 = require("../Util/proxiedPush");
var proxiedSplice_1 = require("../Util/proxiedSplice");
var proxiedUnshift_1 = require("../Util/proxiedUnshift");
var spawnIndexPortals_1 = require("../Util/spawnIndexPortals");
var ArrayType_1 = require("./Constants/ArrayType");
var LENGTH_MUTATORS = ['push', 'pop', 'splice', 'unshift', 'shift'];
var FILL_COUNT = 999;
var undef = undefined;
/**
 * Where necessary, replaces a native array prototype method with a proxied version.
 *
 * This can be necessary for any of the following reasons:
 * 1. To ensure that when the consumer interrogates or manipulates the contents of an array
 *   (e.g `.filter()`, `.map()`), they are operating on proxies wherever they exist to ensure
 *   that reads/writes can be tracked and portals spawned as needed.
 * 2. To ensure that any data returned to the consumer inside an action (e.g. `.find()`, `.map()`)
 *   remains in its proxy form so that any subsequent reads/writes can be tracked.
 * 3. To ensure that any new data inserted into an array (e.g. `.push()`, `.unshift()`, `.splice()`)
 *    is seen and proxied (in the case of maps only).
 *
 * @param methodName The name of the array prototype method to be proxied.
 * @param arr A reference to the underlying array.
 * @param proxyNode A refernce to the proxy node associated with the array.
 * @param args One or more arguments to be passed to the array method.
 */
var proxyArrayMethodImplementation = function (methodName, arr, proxyNode, args) {
    var proxy = proxyNode.proxy;
    switch (methodName) {
        case 'filter':
            return proxiedFilter_1.default(arr, proxy, args);
        case 'find':
            return proxiedFind_1.default(arr, proxy, args);
        case 'forEach':
            return void proxiedMap_1.default(arr, proxy, args);
        case 'includes':
            return proxiedIndexOf_1.default(arr, proxy, args) > -1;
        case 'indexOf':
            return proxiedIndexOf_1.default(arr, proxy, args);
        case 'map':
            return proxiedMap_1.default(arr, proxy, args);
        case 'push':
            // Use native methods only when we can be sure that the array contains
            // no nested proxies
            return proxy._isPrimativeType
                ? arr.push.apply(arr, args)
                : proxiedPush_1.default(arr, proxy, args);
        case 'unshift':
            return proxy._isPrimativeType
                ? arr.unshift.apply(arr, args)
                : proxiedUnshift_1.default(arr, proxyNode, args);
        case 'splice':
            return proxy._isPrimativeType
                ? arr.splice.apply(arr, args)
                : proxiedSplice_1.default(arr, proxyNode, args);
        default:
            // NB: some less common or more modern methods (e.g. `.entries()` are not currently proxied
            return arr[methodName].apply(arr, args);
    }
};
/**
 * Creates a shared prototype for all array proxy instances in the interest
 * of reducing the memory footprint of each proxy instance.
 *
 * The first 1000 indices are filled with functionality to kick off
 * portal spawning, which is a precursor to a proxy being created at a
 * particular index and reduces the risk of a lookup failing.
 */
var createArrayProxyPrototype = function () {
    var proto = {
        _type: ArrayType_1.default.UNKNOWN,
        _isProxy: true,
        get _isArrayType() {
            return this._type === ArrayType_1.default.ARRAY;
        },
        get _isDynamicType() {
            return this._type === ArrayType_1.default.DYNAMIC_OBJECT;
        },
        get _isUnknownType() {
            return this._type === ArrayType_1.default.UNKNOWN;
        },
        get _isPrimativeType() {
            return this._type === ArrayType_1.default.PRIMATIVE;
        },
        get _hasProxyableType() {
            return ([ArrayType_1.default.UNKNOWN, ArrayType_1.default.PRIMATIVE].indexOf(this._type) < 0);
        },
        get _isStaticType() {
            return ([ArrayType_1.default.STATIC_OBJECT, ArrayType_1.default.ARRAY].indexOf(this._type) >
                -1);
        }
    };
    var i = 0;
    while (i < FILL_COUNT) {
        (function (index) {
            return void Object.defineProperty(proto, index, {
                set: function (value) {
                    this.n(index, value);
                },
                get: function () {
                    return this.n(index);
                }
            });
        })(i);
        i++;
    }
    return proto;
};
var arrayProxyProto = createArrayProxyPrototype();
/**
 * Creates a "proxy" for an array including every array prototype
 * method, length, and a dynamic set of index accessors ("portals").
 *
 * Each index portal's accessor is mapped to its respective path
 * in order to optimise array mutations by ensurating that only the
 * mutated elements in an array are "patched".
 */
var createArrayProxy = function (store, value, path) {
    var proxy = Object.create(arrayProxyProto);
    var proxyNode = {
        proxy: proxy,
        children: null
    };
    var proxyParams = {
        store: store,
        path: path,
        proxyNode: proxyNode
    };
    var arrayMembers = Object.getOwnPropertyNames(Array.prototype);
    var portalCount = 0;
    var proxyArrayMethod = function (methodName) {
        var args = [];
        for (var _i = 1; _i < arguments.length; _i++) {
            args[_i - 1] = arguments[_i];
        }
        var couldMutateLength = LENGTH_MUTATORS.indexOf(methodName) > -1;
        var arr = store.readPropertyValue(path, true, couldMutateLength);
        var portalCountAtStart = portalCount;
        var ret = proxyArrayMethodImplementation(methodName, arr, proxyNode, args);
        // If the portal count has already increased as a result of an array
        // method call, then additional spawning is not needed.
        var hasSpawned = portalCountAtStart < portalCount;
        if (couldMutateLength &&
            ['pop', 'shift'].indexOf(methodName) < 0 &&
            !hasSpawned) {
            // If a `.splice()` but no items added, do not spawn
            if (methodName === 'splice' && args[2] === undef)
                return ret;
            // Ensures that all defined indices in the array have getters and
            // setters (portals) whenever the underlying array's length is
            // mutated.
            portalCount = spawnIndexPortals_1.default(proxyParams, portalCount);
        }
        return ret;
    };
    var proxyIndexAssignmentOrLookup = function (index, newValue) {
        // A proxy is not yet in place at this index. Spawn portals until it is.
        portalCount = spawnIndexPortals_1.default(proxyParams, portalCount, {
            itemValue: newValue,
            assignmentIndex: index
        });
        if (newValue !== undef) {
            // Write: A proxy is in place, so we can now safely assign and patch
            proxy[index] = newValue;
        }
        else {
            // Read: A proxy is in place, so we can now safely lookup and patch
            return store.readPropertyValue(path.concat(index.toString()), false, true);
        }
    };
    // Decorate proxy with all array prototype methods
    for (var _i = 0, arrayMembers_1 = arrayMembers; _i < arrayMembers_1.length; _i++) {
        var memberName = arrayMembers_1[_i];
        var method = Array.prototype[memberName];
        if (memberName === 'constructor' || typeof method !== 'function')
            continue;
        proxy[memberName] = proxyArrayMethod.bind(null, memberName);
    }
    // Expose a generic method for index assignment
    proxy.n = proxyIndexAssignmentOrLookup;
    // Expose a means of revealing the underlying value
    proxy.valueOf = function () { return store.readPropertyValue(path, true); };
    // Proxy the array's `length` property
    Object.defineProperties(proxy, {
        length: {
            get: function () { return store.readPropertyValue(path, true).length; },
            set: function (length) {
                return (store.readPropertyValue(path, true, true).length = length);
            }
        }
    });
    if (value && value.length) {
        // An array has been provided with existing items within
        // it, perform an initial spawn
        portalCount = spawnIndexPortals_1.default(proxyParams, portalCount, {
            prepopulatedArray: value
        });
    }
    return proxyNode;
};
exports.default = createArrayProxy;
//# sourceMappingURL=createArrayProxy.js.map